import { LoadingState } from '../../components/feedback/LoadingState'
import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { titleTheme, pageTheme, headerTheme, contentTheme, subTheme, cardTheme } from '../../components/consoleTheme'

import { useState, useEffect } from "react"
import styled from "styled-components"
import Inventory from "./Inventory"
import Clusters from "./Clusters"
import { Plus, RefreshCw, Trash2, Copy, Eye, EyeOff, KeyRound, Boxes, Tag, HardDrive, BookOpen, Terminal } from "lucide-react"

const API = "/api/registry"
function getToken() { return sessionStorage.getItem("token") || localStorage.getItem("token") }
function authHeaders(json = true) {
  const h = {}
  if (json) h["Content-Type"] = "application/json"
  const t = getToken()
  if (t) h["Authorization"] = `Bearer ${t}`
  return h
}
function fmtBytes(n) {
  if (!n || n < 0) return "-"
  const u = ["B", "KB", "MB", "GB", "TB"]
  let i = 0, v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`
}
function copyText(t) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(t).catch(() => fallbackCopy(t))
  } else fallbackCopy(t)
}
function fallbackCopy(t) {
  const ta = document.createElement("textarea")
  ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0"
  document.body.appendChild(ta); ta.focus(); ta.select()
  try { document.execCommand("copy") } catch (_) {}
  document.body.removeChild(ta)
}

const PageContainer = styled.div`
  ${pageTheme}
padding:0; background:#f8fafc; min-height:100vh;`
const Header = styled.header`
  ${headerTheme}

  display:flex; align-items:center; justify-content:space-between;
  padding:1rem 2rem; background:#fff; border-bottom:1px solid #e2e8f0;
  @media(max-width:768px){padding:1rem;flex-wrap:wrap;gap:.75rem;align-items:flex-start;}`
const Title = styled.h1`
 ${titleTheme}
font-size:1.25rem; font-weight:600; color:#1e293b;`
const Sub = styled.p`
  ${subTheme}
margin:.25rem 0 0; font-size:.8rem; color:#94a3b8;`
const Button = styled.button`
  display:inline-flex; align-items:center; gap:.5rem; padding:.5rem 1rem;
  border-radius:.375rem; font-weight:500; cursor:pointer;
  ${(p) => p.primary
    ? `background:var(--accent);color:#fff;border:none;&:hover{background:#115855;}`
    : `background:#fff;color:#64748b;border:1px solid #e2e8f0;&:hover{background:#f8fafc;}`}
  &:disabled{opacity:.6;cursor:default;}`
const Content = styled.div`
  ${contentTheme}
padding:2rem;@media(max-width:768px){padding:1rem;}`
const Card = styled.div`
  ${cardTheme}
background:#fff; border:1px solid #e2e8f0; border-radius:.5rem; overflow:hidden; margin-bottom:1.5rem;`
const CardHead = styled.div`padding:.9rem 1.5rem; border-bottom:1px solid #f1f5f9; font-weight:600; color:#334155; display:flex; align-items:center; gap:.5rem;`
const CardBody = styled.div`padding:1.25rem 1.5rem;`
const Mono = styled.code`font-family:ui-monospace,Menlo,Consolas,monospace; font-size:.82rem; color:#334155;`
const Empty = styled.div`padding:2rem; text-align:center; color:#94a3b8;`
const RowHead = styled.div`
  display:grid; grid-template-columns:2fr 1fr 1fr 1fr .6fr; gap:.5rem;
  padding:.85rem 1.5rem; border-bottom:1px solid #e2e8f0; font-weight:600; color:#475569; font-size:.85rem;
  @media(max-width:768px){display:none;}`
const Row = styled.div`
  display:grid; grid-template-columns:2fr 1fr 1fr 1fr .6fr; gap:.5rem;
  padding:.9rem 1.5rem; border-bottom:1px solid #f1f5f9; align-items:center;
  &:last-child{border-bottom:none;}
  @media(max-width:768px){
    grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:.65rem;padding:1rem;
    border-bottom:8px solid #f8fafc;
    > *{min-width:0;overflow-wrap:anywhere;}
    > :first-child, > :last-child{grid-column:1/-1;}
    > [data-label]::before{content:attr(data-label);display:block;margin-bottom:.2rem;color:#94a3b8;font-size:.7rem;font-weight:600;}
  }`
const IconBtn = styled.button`
  background:none; border:1px solid #e2e8f0; border-radius:6px; padding:4px 8px; cursor:pointer; color:#64748b;
  display:inline-flex; align-items:center; gap:4px; font-size:.78rem;
  &:hover{color:var(--accent);border-color:var(--accent);}`

const Cmd = styled.div`
  display:flex; align-items:center; justify-content:space-between; gap:.75rem;
  background:#0f172a; color:#e2e8f0; border-radius:6px; padding:.6rem .85rem; margin:.35rem 0;
  font-family:ui-monospace,Menlo,Consolas,monospace; font-size:.8rem; overflow-x:auto;
  button{background:none;border:none;color:#94a3b8;cursor:pointer;flex-shrink:0;&:hover{color:#fff;}}`
const CredBox = styled.div`
  display:flex; flex-wrap:wrap; gap:1.25rem; align-items:center;
  background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:1rem 1.25rem;`
const Field = styled.div`display:flex; flex-direction:column; gap:.2rem;
  span{font-size:.72rem;color:#94a3b8;} strong{font-size:.9rem;color:#1e293b;font-family:ui-monospace,Menlo,Consolas,monospace;}`
const FormInput = styled.input`
  padding:.55rem .75rem; border:1px solid #e2e8f0; border-radius:6px; outline:none; font-size:.9rem;
  &:focus{border-color:var(--accent);}`

const TabBar = styled.div`overflow-y:hidden;display:flex; gap:.25rem; border-bottom:1px solid #e2e8f0; margin-bottom:1.5rem;overflow-x:auto;max-width:100%;`
const TabBtn = styled.button`
  display:inline-flex; align-items:center; gap:.4rem; padding:.6rem 1rem; background:none; border:none; cursor:pointer;
  font-size:.9rem; font-weight:${(p) => (p.$active ? 600 : 500)}; color:${(p) => (p.$active ? "var(--accent)" : "#64748b")};
  border-bottom:2px solid ${(p) => (p.$active ? "var(--accent)" : "transparent")}; margin-bottom:-1px;
  &:hover{color:var(--accent);}`
const Step = styled.div`margin-bottom:1.4rem;`
const StepTitle = styled.h3`
  display:flex; align-items:center; gap:.6rem; font-size:.95rem; font-weight:700; color:#1e293b; margin:0 0 .5rem;
  .n{display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;
     background:var(--accent);color:#fff;font-size:.78rem;flex-shrink:0;}`
const StepBody = styled.div`padding-left:2rem; color:#475569; font-size:.88rem; line-height:1.6;
  ul{margin:.3rem 0;padding-left:1.1rem;} li{margin:.15rem 0;} a{color:var(--accent);}`
const Note = styled.div`
  display:flex; gap:.6rem; padding:.85rem 1rem; border-radius:8px; font-size:.84rem; line-height:1.55;
  background:${(p) => (p.$warn ? "#fffbeb" : "#eff6ff")}; border:1px solid ${(p) => (p.$warn ? "#fde68a" : "#bfdbfe")};
  color:${(p) => (p.$warn ? "#92400e" : "#1e40af")}; margin:.4rem 0;`

function GuideCmd({ children }) {
  return (
    <Cmd>
      <span>{children}</span>
      <button onClick={() => copyText(children)} title="복사"><Copy size={14} /></button>
    </Cmd>
  )
}

function Guide({ host, ns }) {
  const N = ns || "<네임스페이스>"
  return (
    <Card>
      <CardHead><BookOpen size={16} /> 컨테이너 레지스트리 사용 가이드</CardHead>
      <CardBody>
        <Note>
          <Terminal size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            아래 명령은 <strong>Docker</strong>와 <strong>Podman</strong> 모두 동일하게 동작합니다(<code>docker</code>를 <code>podman</code>으로 바꿔도 됨).
            레지스트리 주소는 <Mono>{host}</Mono>, 이미지 경로는 <Mono>{host}/{N}/&lt;이미지&gt;:&lt;태그&gt;</Mono> 형식입니다.
          </span>
        </Note>

        <Step>
          <StepTitle><span className="n">1</span> 사전 준비 — Docker / Podman 설치</StepTitle>
          <StepBody>
            <ul>
              <li>Windows · macOS: <a href="https://www.docker.com/products/docker-desktop/" target="_blank" rel="noreferrer">Docker Desktop</a> 설치</li>
              <li>Linux: <Mono>sudo apt install -y podman</Mono> 또는 docker 설치</li>
            </ul>
            별도의 insecure-registry 설정은 필요 없습니다 — 정식 TLS(Let's Encrypt) 인증서를 사용합니다.
          </StepBody>
        </Step>

        <Step>
          <StepTitle><span className="n">2</span> 네임스페이스 등록</StepTitle>
          <StepBody>
            {ns
              ? <>이미 <Mono>{ns}</Mono> 네임스페이스가 등록되어 있습니다. “내 이미지 업로드” 탭에서 로그인 계정/비밀번호를 확인하세요.</>
              : <>“내 이미지 업로드” 탭에서 사용할 네임스페이스를 등록하면 전용 <strong>로그인 계정과 비밀번호</strong>가 발급됩니다.</>}
          </StepBody>
        </Step>

        <Step>
          <StepTitle><span className="n">3</span> 로그인</StepTitle>
          <StepBody>
            <GuideCmd>{`docker login ${host} -u ${N}`}</GuideCmd>
            비밀번호를 입력하면 <Mono>Login Succeeded</Mono>가 표시됩니다.
          </StepBody>
        </Step>

        <Step>
          <StepTitle><span className="n">4</span> 이미지 빌드 · 태그</StepTitle>
          <StepBody>
            Dockerfile에서 바로 빌드:
            <GuideCmd>{`docker build -t ${host}/${N}/myapp:latest .`}</GuideCmd>
            또는 기존 이미지를 태깅:
            <GuideCmd>{`docker tag nginx:latest ${host}/${N}/nginx:latest`}</GuideCmd>
          </StepBody>
        </Step>

        <Step>
          <StepTitle><span className="n">5</span> 푸시 (업로드)</StepTitle>
          <StepBody>
            <GuideCmd>{`docker push ${host}/${N}/myapp:latest`}</GuideCmd>
            업로드 후 “내 이미지 업로드” 탭 목록에 리포지토리·태그·크기가 표시됩니다.
          </StepBody>
        </Step>

        <Step>
          <StepTitle><span className="n">6</span> 풀 (다운로드) · 실행</StepTitle>
          <StepBody>
            <GuideCmd>{`docker pull ${host}/${N}/myapp:latest`}</GuideCmd>
            <GuideCmd>{`docker run -d ${host}/${N}/myapp:latest`}</GuideCmd>
          </StepBody>
        </Step>

        <Step>
          <StepTitle><span className="n">7</span> 삭제</StepTitle>
          <StepBody>“내 이미지 업로드” 탭 목록에서 휴지통 버튼으로 태그를 삭제합니다. 본인 네임스페이스의 이미지만 삭제할 수 있습니다.</StepBody>
        </Step>

        <Step>
          <StepTitle><span className="n">8</span> 멀티아키텍처 (선택)</StepTitle>
          <StepBody>
            여러 CPU 아키텍처용 이미지는 buildx로 한 번에 푸시:
            <GuideCmd>{`docker buildx build --platform linux/amd64,linux/arm64 -t ${host}/${N}/myapp:latest --push .`}</GuideCmd>
          </StepBody>
        </Step>

        <StepTitle style={{ paddingLeft: 0, marginTop: "1.5rem" }}>문제 해결</StepTitle>
        <Note $warn>
          <span>
            <strong>로그인/푸시 실패 · “can't talk to a V1 container registry”</strong> — 비밀번호 재발급 직후 레지스트리가 갱신 중일 수 있습니다.
            몇 초 후 다시 시도하세요. 계속되면 비밀번호가 맞는지 확인합니다.
          </span>
        </Note>
        <Note $warn>
          <span><strong>비밀번호 분실</strong> — “내 이미지 업로드” 탭의 <em>비밀번호 재발급</em>으로 새 비밀번호를 발급받으세요(기존 비밀번호는 무효화됩니다).</span>
        </Note>
        <Note>
          <span>
            <strong>격리</strong> — 네임스페이스별로 로그인 계정이 분리됩니다. 레포지토리 단위 세부 권한(RBAC)은 추후 token-auth/Harbor 승격 시 강화됩니다.
          </span>
        </Note>
      </CardBody>
    </Card>
  )
}

const ContainerRegistry = () => {
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState("")
  const [ns, setNs] = useState("")
  const [busy, setBusy] = useState("")
  const [showPw, setShowPw] = useState(false)
  const [view, setView] = useState(() => { const tab = new URLSearchParams(window.location.search).get('tab'); return ['clusters', 'registry'].includes(tab) ? tab : 'overview' })
  const [refreshKey, setRefreshKey] = useState(0)
  const load = async () => {
    setLoading(true); setErr("")
    try {
      const res = await fetch(`${API}/me`, { headers: authHeaders(false), credentials: "include" })
      if (res.status === 401 || res.status === 403) { setErr("로그인이 필요합니다."); setInfo(null); return }
      if (!res.ok) throw Error("저장소 정보를 불러오지 못했습니다.")
      const data = await res.json()
      if (typeof data.registered !== "boolean") throw Error("저장소 응답을 확인할 수 없습니다.")
      setInfo(data)
    } catch (e) { setErr("정보를 불러오지 못했습니다.") }
    finally { setLoading(false) }
  }
  useEffect(() => { if (view === "images") load() }, [view])

  const register = async () => {
    const name = ns.trim().toLowerCase()
    if (!/^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/.test(name) || name.length < 2) {
      alert("네임스페이스는 소문자/숫자와 -._ 만, 2~30자입니다."); return
    }
    setBusy("register")
    try {
      const res = await fetch(`${API}/register`, {
        method: "POST", headers: authHeaders(), credentials: "include",
        body: JSON.stringify({ namespace: name }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { alert("등록 실패: " + (data.error || res.status)); return }
      setShowPw(true); setNs(""); load()
    } catch (e) { alert("등록 실패: " + e.message) }
    finally { setBusy("") }
  }

  const regenerate = async () => {
    if (!window.confirm("비밀번호를 재발급하면 기존 비밀번호로는 로그인할 수 없습니다. 진행할까요?")) return
    setBusy("regen")
    try {
      const res = await fetch(`${API}/regenerate`, { method: "POST", headers: authHeaders(false), credentials: "include" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { alert("재발급 실패: " + (data.error || res.status)); return }
      setShowPw(true); load()
    } catch (e) { alert("재발급 실패: " + e.message) }
    finally { setBusy("") }
  }

  const delTag = async (repo, tag) => {
    if (!window.confirm(`${repo}:${tag} 이미지를 삭제할까요?`)) return
    setBusy(`${repo}:${tag}`)
    try {
      const res = await fetch(`${API}/delete-tag`, {
        method: "POST", headers: authHeaders(), credentials: "include",
        body: JSON.stringify({ repo, tag }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { alert("삭제 실패: " + (data.error || res.status)); return }
      load()
    } catch (e) { alert("삭제 실패: " + e.message) }
    finally { setBusy("") }
  }

  const host = info?.public_host || "skynet.re.kr"
  const registered = info?.registered
  const repos = info?.repositories || []
  const exampleRepo = repos[0]?.name?.split("/").slice(1).join("/") || "myapp"

  return (
    <PageContainer>
      <Header>
        <div>
          <Title>이미지·컨테이너</Title>
          <Sub>운영·개발 환경의 실제 이미지와 실행 현황을 확인합니다.</Sub>
        </div>
        <div style={{ display: "flex", gap: ".5rem" }}>
          <Button onClick={() => view === "images" ? load() : setRefreshKey(value => value + 1)}><RefreshCw size={16} /> 새로고침</Button>
        </div>
      </Header>

      <Content>
        <TabBar>
          <TabBtn $active={view === "overview"} onClick={() => setView("overview")}><Boxes size={15} /> 실행 현황</TabBtn>
          <TabBtn $active={view === "registry"} onClick={() => setView("registry")}><HardDrive size={15} /> 이미지 저장소</TabBtn>
          <TabBtn $active={view === "clusters"} onClick={() => setView("clusters")}><Boxes size={15} /> 클러스터 관리</TabBtn>
          <TabBtn $active={view === "images"} onClick={() => setView("images")}><Plus size={15} /> 내 이미지 업로드</TabBtn>
          <TabBtn $active={view === "guide"} onClick={() => setView("guide")}><BookOpen size={15} /> 사용 가이드</TabBtn>
        </TabBar>

        {view === "overview" ? <Inventory refreshKey={refreshKey} copy={copyText} formatBytes={fmtBytes} />
          : view === "registry" ? <Inventory mode="registry" key="registry" refreshKey={refreshKey} copy={copyText} formatBytes={fmtBytes} />
          : view === "clusters" ? <Clusters key={refreshKey} />
          : view === "guide" ? <Guide host={host} ns={info?.namespace} />
          : loading ? <LoadingState label="불러오는 중…" />
          : err ? <Empty>{err}</Empty>
          : !registered ? (
            <Card>
              <CardHead><Boxes size={16} /> 내 이미지 저장소 만들기</CardHead>
              <CardBody>
                <p style={{ margin: "0 0 .9rem", color: "#64748b", fontSize: ".88rem" }}>
                  직접 만든 이미지를 올릴 때 사용하는 선택 설정입니다. 운영 현황을 보는 데에는 등록이 필요하지 않습니다.
                  이미지 주소는 <Mono>{host}/&lt;네임스페이스&gt;/&lt;이미지&gt;:&lt;태그&gt;</Mono> 형태가 됩니다.
                </p>
                <div style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap", maxWidth: 460 }}>
                  <span style={{ color: "#94a3b8", fontFamily: "ui-monospace,monospace", fontSize: ".85rem" }}>{host}/</span>
                  <FormInput aria-label="새 이미지 저장소 이름" placeholder="예: my-app" maxLength={30} value={ns} onChange={(e) => setNs(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && register()} style={{ flex: "1 1 180px", minWidth: 0 }} />
                  <Button primary onClick={register} disabled={busy === "register"}>
                    <Plus size={16} /> {busy === "register" ? "등록 중…" : "등록"}
                  </Button>
                </div>
              </CardBody>
            </Card>
          ) : (
            <>
              {/* 자격 + 사용법 */}
              <Card>
                <CardHead><KeyRound size={16} /> 내 레지스트리 — <Mono>{host}/{info.namespace}</Mono></CardHead>
                <CardBody>
                  <CredBox>
                    <Field><span>네임스페이스</span><strong>{info.namespace}</strong></Field>
                    <Field><span>로그인 계정</span><strong>{info.username}</strong></Field>
                    <Field>
                      <span>비밀번호</span>
                      <strong style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {showPw ? info.password : "••••••••••"}
                        <IconBtn onClick={() => setShowPw((v) => !v)} title={showPw ? "숨기기" : "보기"}>
                          {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
                        </IconBtn>
                        <IconBtn onClick={() => copyText(info.password)} title="복사"><Copy size={13} /></IconBtn>
                      </strong>
                    </Field>
                    <div style={{ marginLeft: "auto" }}>
                      <IconBtn onClick={regenerate} disabled={busy === "regen"} title="비밀번호 재발급">
                        <RefreshCw size={13} /> {busy === "regen" ? "재발급 중…" : "비밀번호 재발급"}
                      </IconBtn>
                    </div>
                  </CredBox>

                  <p style={{ margin: "1.1rem 0 .3rem", fontWeight: 600, color: "#334155", fontSize: ".88rem" }}>
                    푸시 방법 <span style={{ fontWeight: 400, color: "#94a3b8" }}>(docker / podman 동일)</span>
                  </p>
                  {[
                    `podman login ${host} -u ${info.username}`,
                    `podman tag <로컬이미지> ${host}/${info.namespace}/${exampleRepo}:latest`,
                    `podman push ${host}/${info.namespace}/${exampleRepo}:latest`,
                  ].map((c, i) => (
                    <Cmd key={i}><span>{c}</span><button onClick={() => copyText(c)} title="복사"><Copy size={14} /></button></Cmd>
                  ))}
                  <p style={{ margin: ".9rem 0 .3rem", fontWeight: 600, color: "#334155", fontSize: ".88rem" }}>풀 방법</p>
                  <Cmd>
                    <span>{`podman pull ${host}/${info.namespace}/${exampleRepo}:latest`}</span>
                    <button onClick={() => copyText(`podman pull ${host}/${info.namespace}/${exampleRepo}:latest`)} title="복사"><Copy size={14} /></button>
                  </Cmd>
                </CardBody>
              </Card>

              {/* 이미지 목록 */}
              <Card>
                <CardHead><Boxes size={16} /> 이미지 ({repos.reduce((a, r) => a + r.tag_count, 0)} 태그 / {fmtBytes(repos.reduce((a, r) => a + r.size, 0))})</CardHead>
                <RowHead>
                  <span>리포지토리 : 태그</span><span>다이제스트</span><span><Tag size={12} /> 태그</span><span><HardDrive size={12} /> 크기</span><span>동작</span>
                </RowHead>
                {repos.length === 0 ? (
                  <Empty>이 계정의 이미지 저장소에는 아직 푸시된 이미지가 없습니다. 실행 중인 Pod·컨테이너는 “실행 현황” 탭에서 확인하세요.</Empty>
                ) : repos.flatMap((repo) =>
                  (repo.tags.length ? repo.tags : [{ tag: "(태그 없음)", size: 0, digest: null }]).map((t) => (
                    <Row key={`${repo.name}:${t.tag}`}>
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>
                        {repo.name.split("/").slice(1).join("/") || repo.name}
                        <span style={{ color: "#64748b", fontWeight: 400 }}> : {t.tag}</span>
                      </span>
                      <Mono data-label="다이제스트" title={t.digest || ""}>{t.digest ? t.digest.replace("sha256:", "").slice(0, 12) : "-"}</Mono>
                      <span data-label="태그" style={{ color: "#475569", fontSize: ".85rem" }}>{repo.tag_count}</span>
                      <span data-label="크기" style={{ color: "#475569", fontSize: ".85rem" }}>{fmtBytes(t.size)}</span>
                      <span>
                        {t.digest && (
                          <IconBtn style={{ borderColor: "#fecaca", color: "#ef4444" }}
                            onClick={() => delTag(repo.name, t.tag)} disabled={busy === `${repo.name}:${t.tag}`} title="삭제">
                            <Trash2 size={13} />
                          </IconBtn>
                        )}
                      </span>
                    </Row>
                  ))
                )}
              </Card>

              <p style={{ color: "#94a3b8", fontSize: ".78rem", margin: 0 }}>
                ※ 인증된 자체호스팅 레지스트리(MVP)입니다. 네임스페이스는 계정별로 분리되지만 레포 단위 접근제어(RBAC)는
                추후 token-auth/Harbor 승격 시 강화됩니다.
              </p>
            </>
          )}
      </Content>
    </PageContainer>
  )
}

export default ContainerRegistry
